import { useNavigate } from 'react-router-dom';
import { DriversStatisticsByVehicle } from '../../../API/types/Drivers';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import Tags from '../Tags';
import { ListItemWrapper } from '../../ui/ListItemWrapper/ListItemWrapper';
import { getFormattedDate, convertMsToTime } from '../../../utils/dateUtils';
import { generateDistance } from '../../../utils/unitsUtils';
import { IconKeys } from '../Icon/IconKeys';
import { generateScoreColor, getScoreLabel } from '../../../utils/commonUtils';
import { useEntityScores } from '../../../API/hooks/useEntityScores';
import useGetFleetId from '../../../API/hooks/useGetFleetId';

const PERIOD_TO_SCORE_FIELD: Record<string, 'score' | 'scoreDaily' | 'scoreWeekly' | 'scoreMonthly'> = {
  daily: 'scoreDaily',
  weekly: 'scoreWeekly',
  monthly: 'scoreMonthly',
};

type Props = {
  item?: DriversStatisticsByVehicle;
  period?: string;
};
function DriverStatsItem({ item, period }: Props): JSX.Element {
  const navigate = useNavigate();
  const driverThumbnail = useDriverThumbnail();
  const fleetId = useGetFleetId();

  const {
    driverId,
    driver,
    distanceInKm,
    alertCount,
    durationInMs,
  } = item!;

  const scoreField = PERIOD_TO_SCORE_FIELD[period ?? ''] ?? 'score';
  const { scores } = useEntityScores(fleetId, driverId ? [driverId] : [], 'driver');
  const computedScores = driverId ? scores.get(driverId) ?? null : null;
  const computedScore = computedScores ? (computedScores[scoreField] ?? null) : null;
  const displayScore = computedScore !== null ? computedScore : (driver?.score ?? null);
  const periodLabel = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }[period ?? ''] ?? 'All time';

  return (
    <ListItemWrapper
      onClick={driverId ? () => navigate(`/drivers/${driverId}`) : undefined}
      noclick={!driverId}
    >
      {displayScore !== null && displayScore > 0 && (
        <Tags.Status label={`${getScoreLabel(displayScore)} · ${periodLabel}`} backgroundColor={generateScoreColor(displayScore)} />
      )}
      <Tags.ItemOverview
        avatar={driver?.avatarUrl || driverThumbnail}
        avatarAlt="driver"
        title={driver ? `${driver?.firstName} ${driver?.lastName}` : 'Unassigned driver'}
        subtitle={driver?.licenseName && driver?.licenseExpireAt ? `${driver?.licenseName} (exp. ${driver?.licenseExpireAt ? getFormattedDate(driver?.licenseExpireAt) : ''})` : ''}
        isRoundImage
      />
      <Tags.LabeledIcon
        flexible
        label={`${convertMsToTime(durationInMs)} driving time`}
        icon={IconKeys.TIME}
      />
      <Tags.LabeledIcon
        label={generateDistance(distanceInKm, false)}
        icon={IconKeys.DISTANCE}
      />
      <Tags.LabeledIcon
        flexible
        label={String(alertCount)}
        icon={IconKeys.ALERT_CIRCLE}
      />
    </ListItemWrapper>
  );
}

export default DriverStatsItem;
